import {
  CANNONS,
  CARS,
  DEATH_EXPLOSIONS,
  ENGINE_SOUNDS,
  NITRO_EFFECTS,
  PROJECTILES,
  TOMBSTONES,
  WHEELS,
} from './data.js';
import { sanitizeGarage } from './garage.js';
import {
  getSupabaseAuthRedirectUrl,
  getSupabaseClient,
  isSupabaseConfigured,
} from '../services/supabaseClient.js';

const ENTITLEMENT_TYPES = Object.freeze({
  vehicle: { key:'ownedCars', catalog:CARS },
  wheel: { key:'ownedWheels', catalog:WHEELS },
  engine_sound: { key:'ownedEngineSounds', catalog:ENGINE_SOUNDS },
  cannon: { key:'ownedCannons', catalog:CANNONS },
  projectile: { key:'ownedProjectiles', catalog:PROJECTILES },
  nitro: { key:'ownedNitros', catalog:NITRO_EFFECTS },
  explosion: { key:'ownedExplosions', catalog:DEATH_EXPLOSIONS },
  tombstone: { key:'ownedTombstones', catalog:TOMBSTONES },
});

function messageOf(error, fallback='No se pudo sincronizar la cuenta.'){
  return error?.message||fallback;
}

function buildCloudGarage(entitlements, loadouts){
  const ownedIds=new Set(entitlements.map((entry)=>entry.item_id));
  const source={loadouts:{}};
  for(const definition of Object.values(ENTITLEMENT_TYPES)){
    source[definition.key]=definition.catalog
      .filter((item)=>ownedIds.has(item.id))
      .map((item)=>item.id);
  }
  for(const row of loadouts){
    source.loadouts[row.car_id]={
      wheelId:row.wheel_id,
      engineSoundId:row.engine_sound_id,
      cannonId:row.cannon_id,
      projectileId:row.projectile_id,
      nitroId:row.nitro_id,
      explosionId:row.explosion_id,
      tombstoneId:row.tombstone_id,
    };
  }
  // Para una cuenta autenticada, Supabase es la única autoridad. Incluso los
  // starters llegan como entitlements reconciliados por initialize_player_garage.
  return sanitizeGarage(source,{includeStarters:false});
}

export class PlayerAccount {
  constructor({onStateChange=()=>{},onGarageChange=()=>{}}={}){
    this.client=getSupabaseClient();
    this.onStateChange=onStateChange;
    this.onGarageChange=onGarageChange;
    this.localGarage=null;
    this.localSelectedCarId=null;
    this.authSubscription=null;
    this.syncPromise=null;
    this.loadoutQueue=new Map();
    this.loadoutTimer=0;
    this.selectedCarTimer=0;
    this.accessToken=null;
    this.state={
      configured:isSupabaseConfigured(),
      status:'guest',
      syncStatus:'local',
      user:null,
      profile:null,
      wallet:{currency:'crowns',balance:0},
      economyAvailable:false,
      message:isSupabaseConfigured()?'Jugando como invitado.':'Supabase no está configurado.',
    };
  }

  snapshot(){
    return {...this.state};
  }

  emit(patch={}){
    this.state={...this.state,...patch};
    this.onStateChange(this.snapshot());
  }

  async initialize(localGarage, selectedCarId){
    this.localGarage=localGarage;
    this.localSelectedCarId=selectedCarId;
    this.emit();
    if(!this.client)return this.snapshot();

    const {data,error}=await this.client.auth.getSession();
    if(error){
      this.emit({status:'guest',syncStatus:'error',message:messageOf(error)});
    }else if(data.session){
      await this.activateSession(data.session);
    }

    const {data:listener}=this.client.auth.onAuthStateChange((event,session)=>{
      if(event==='SIGNED_OUT'){
        this.accessToken=null;
        this.emit({
          status:'guest',
          syncStatus:'local',
          user:null,
          profile:null,
          wallet:{currency:'crowns',balance:0},
          economyAvailable:false,
          message:'Sesión cerrada. El garage local sigue disponible.',
        });
        return;
      }
      if(event==='SIGNED_IN'&&session&&session.user.id!==this.state.user?.id){
        queueMicrotask(()=>this.activateSession(session));
      }
    });
    this.authSubscription=listener.subscription;
    return this.snapshot();
  }

  async activateSession(session){
    if(this.syncPromise)return this.syncPromise;
    this.syncPromise=this.syncSession(session).finally(()=>{this.syncPromise=null;});
    return this.syncPromise;
  }

  async syncSession(session){
    this.accessToken=session.access_token||null;
    this.emit({
      status:'loading',
      syncStatus:'syncing',
      user:session.user,
      message:'Sincronizando colección y equipamiento…',
    });
    try{
      const {error:initError}=await this.client.rpc('initialize_player_garage',{
        p_local_garage:this.localGarage||{},
        p_selected_car_id:this.localSelectedCarId,
      });
      if(initError)throw initError;
      const {error:economyError}=await this.client.rpc('ensure_player_economy');
      const economyAvailable=!economyError;
      if(economyError)console.warn('La migración de economía todavía no está aplicada.',economyError);

      const [profileResult,entitlementsResult,loadoutsResult,walletResult]=await Promise.all([
        this.client.from('player_profiles')
          .select('user_id,display_name,selected_car_id,updated_at')
          .eq('user_id',session.user.id)
          .single(),
        this.client.from('player_entitlements')
          .select('item_id,source,quantity')
          .eq('user_id',session.user.id)
          .is('revoked_at',null),
        this.client.from('player_loadouts')
          .select('car_id,wheel_id,engine_sound_id,cannon_id,projectile_id,nitro_id,explosion_id,tombstone_id,updated_at')
          .eq('user_id',session.user.id),
        economyAvailable
          ?this.client.from('player_wallets')
            .select('crowns,updated_at')
            .eq('user_id',session.user.id)
            .single()
          :Promise.resolve({data:{crowns:0},error:null}),
      ]);
      const failure=profileResult.error||entitlementsResult.error||loadoutsResult.error||walletResult.error;
      if(failure)throw failure;

      const garage=buildCloudGarage(entitlementsResult.data||[],loadoutsResult.data||[]);
      const selectedCarId=garage.ownedCars.includes(profileResult.data?.selected_car_id)
        ?profileResult.data.selected_car_id
        :garage.ownedCars[0];
      this.localGarage=garage;
      this.localSelectedCarId=selectedCarId;
      this.emit({
        status:'signed-in',
        syncStatus:'synced',
        user:session.user,
        profile:profileResult.data,
        wallet:{currency:'crowns',balance:Number(walletResult.data?.crowns)||0},
        economyAvailable,
        message:'Garage sincronizado.',
      });
      this.onGarageChange({garage,selectedCarId,profile:profileResult.data});
      return garage;
    }catch(error){
      console.warn('No se pudo sincronizar el garage del jugador.',error);
      this.emit({
        status:'signed-in',
        syncStatus:'error',
        user:session.user,
        message:messageOf(error),
      });
      return null;
    }
  }

  async signIn(email,password){
    if(!this.client)throw new Error('Supabase no está configurado en este build.');
    this.emit({status:'loading',syncStatus:'syncing',message:'Iniciando sesión…'});
    const {data,error}=await this.client.auth.signInWithPassword({email,password});
    if(error){
      this.emit({status:'guest',syncStatus:'error',message:messageOf(error,'No se pudo iniciar sesión.')});
      throw error;
    }
    await this.activateSession(data.session);
    return data;
  }

  async signUp(email,password,displayName){
    if(!this.client)throw new Error('Supabase no está configurado en este build.');
    this.emit({status:'loading',syncStatus:'syncing',message:'Creando cuenta…'});
    const {data,error}=await this.client.auth.signUp({
      email,
      password,
      options:{
        data:{display_name:displayName||email.split('@')[0]},
        emailRedirectTo:getSupabaseAuthRedirectUrl(),
      },
    });
    if(error){
      this.emit({status:'guest',syncStatus:'error',message:messageOf(error,'No se pudo crear la cuenta.')});
      throw error;
    }
    if(data.session){
      await this.activateSession(data.session);
    }else{
      this.emit({
        status:'guest',
        syncStatus:'local',
        message:'Cuenta creada. Revisá tu correo para confirmarla y luego iniciá sesión.',
      });
    }
    return data;
  }

  async signOut(){
    if(!this.client)return;
    await this.flushLoadouts();
    const {error}=await this.client.auth.signOut();
    if(error)throw error;
  }

  getOnlineAccount(){
    if(this.state.status!=='signed-in'||!this.state.user?.id||!this.accessToken)return null;
    return {userId:this.state.user.id,accessToken:this.accessToken};
  }

  async purchaseItem(itemId){
    if(!this.client||this.state.status!=='signed-in'){
      throw new Error('Iniciá sesión para canjear artículos con Coronas.');
    }
    if(!this.state.economyAvailable){
      throw new Error('La economía todavía no fue habilitada en este entorno.');
    }
    const idempotencyKey=globalThis.crypto?.randomUUID?.()
      ||`${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.emit({syncStatus:'syncing',message:'Canjeando artículo…'});
    const {data,error}=await this.client.rpc('purchase_catalog_item',{
      p_item_id:itemId,
      p_idempotency_key:idempotencyKey,
    });
    if(error){
      const message=/insufficient crowns/i.test(error.message||'')
        ?'No tenés Coronas suficientes para este artículo.'
        :messageOf(error,'No se pudo completar el canje.');
      this.emit({syncStatus:'error',message});
      throw new Error(message);
    }
    await this.refresh();
    return Array.isArray(data)?data[0]:data;
  }

  async refreshEconomy(){
    if(!this.client||this.state.status!=='signed-in'||!this.state.user?.id||!this.state.economyAvailable)return null;
    const {data,error}=await this.client.from('player_wallets')
      .select('crowns,updated_at')
      .eq('user_id',this.state.user.id)
      .single();
    if(error)throw error;
    const wallet={currency:'crowns',balance:Number(data?.crowns)||0};
    this.emit({wallet});
    return wallet;
  }

  async updateDisplayName(displayName){
    const name=String(displayName||'').trim().slice(0,24);
    if(!this.client||!this.state.user)throw new Error('Iniciá sesión para editar el perfil.');
    if(name.length<2)throw new Error('El nombre debe tener al menos dos caracteres.');
    this.emit({syncStatus:'syncing',message:'Guardando perfil…'});
    const {data,error}=await this.client.rpc('update_player_display_name',{p_display_name:name});
    if(error){
      this.emit({syncStatus:'error',message:messageOf(error)});
      throw error;
    }
    const profile={...this.state.profile,display_name:data};
    this.emit({profile,syncStatus:'synced',message:'Perfil guardado.'});
    return profile;
  }

  saveLoadout(carId,loadout){
    if(!this.client||this.state.status!=='signed-in')return;
    this.loadoutQueue.set(carId,{...loadout});
    clearTimeout(this.loadoutTimer);
    this.emit({syncStatus:'syncing',message:'Guardando equipamiento…'});
    this.loadoutTimer=setTimeout(()=>this.flushLoadouts(),180);
  }

  async flushLoadouts(){
    clearTimeout(this.loadoutTimer);
    if(!this.client||this.state.status!=='signed-in'||!this.loadoutQueue.size)return;
    const entries=[...this.loadoutQueue.entries()];
    this.loadoutQueue.clear();
    const results=await Promise.all(entries.map(([carId,loadout])=>this.client.rpc('save_player_loadout',{
      p_car_id:carId,
      p_wheel_id:loadout.wheelId,
      p_engine_sound_id:loadout.engineSoundId,
      p_cannon_id:loadout.cannonId,
      p_projectile_id:loadout.projectileId,
      p_nitro_id:loadout.nitroId,
      p_explosion_id:loadout.explosionId,
      p_tombstone_id:loadout.tombstoneId,
    })));
    const failure=results.find((result)=>result.error)?.error;
    if(failure){
      console.warn('No se pudo guardar un equipamiento.',failure);
      for(const [carId,loadout] of entries)this.loadoutQueue.set(carId,loadout);
      this.emit({syncStatus:'error',message:messageOf(failure)});
      return;
    }
    this.emit({syncStatus:'synced',message:'Garage sincronizado.'});
  }

  setSelectedCar(carId){
    this.localSelectedCarId=carId;
    if(!this.client||this.state.status!=='signed-in')return;
    clearTimeout(this.selectedCarTimer);
    this.selectedCarTimer=setTimeout(async()=>{
      this.emit({syncStatus:'syncing',message:'Guardando auto seleccionado…'});
      const {error}=await this.client.rpc('set_player_selected_car',{p_car_id:carId});
      this.emit(error
        ?{syncStatus:'error',message:messageOf(error)}
        :{syncStatus:'synced',message:'Garage sincronizado.'});
    },180);
  }

  async refresh(){
    const {data}=await this.client?.auth.getSession()||{data:null};
    if(data?.session)return this.activateSession(data.session);
    return null;
  }
}
